import React, { useState, useEffect } from "react";
import CommonHeading from "../common/CommonHeading";
import { facility, roomItems } from "../data/Data";
import Popup from "../home/popup";
import { useBookings } from "../context/BookingContext";

export default function Rooms() {
  const { rooms } = useBookings();
  const [showPopup, setShowPopup] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState("");
  const [selectedDetailRoom, setSelectedDetailRoom] = useState(null);

  const [localRooms, setLocalRooms] = useState(() => {
    try {
      const stored = localStorage.getItem("hk_rooms");
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    const handleStorage = () => {
      try {
        const stored = localStorage.getItem("hk_rooms");
        if (stored) setLocalRooms(JSON.parse(stored));
      } catch (e) {}
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const getMergedRooms = () => {
    const map = new Map();
    (rooms || []).forEach(r => {
      if (r && r.roomType) map.set(r.roomType.toLowerCase().trim(), r);
    });
    (localRooms || []).forEach(l => {
      if (l && l.roomType) {
        const key = l.roomType.toLowerCase().trim();
        const existing = map.get(key) || {};
        map.set(key, { ...existing, ...l });
      }
    });
    return Array.from(map.values());
  };

  const activeRoomsList = getMergedRooms();

  const getRoomLiveInfo = (roomName) => {
    const matched = activeRoomsList.find(
      (r) => (r.roomType || "").toLowerCase().trim() === (roomName || "").toLowerCase().trim()
    );
    if (!matched) {
      return {
        price: null,
        totalInventory: 10,
        bookedCount: 0,
        available: 10,
        isFull: false,
        status: "Available"
      };
    }

    const total = Number(matched.totalInventory !== undefined ? matched.totalInventory : 10);
    const booked = Number(matched.bookedCount || 0);
    const isStatusFull = matched.status === "Full";
    const avail = isStatusFull ? 0 : Math.max(0, total - booked);

    const discPct = Number(matched.discountPercent || 0);
    const pText = matched.promoText || (discPct > 0 ? `${discPct}% OFF TODAY` : "");
    const rawPrice = matched.pricePerNight ? Number(matched.pricePerNight) : null;
    const discountedPrice = rawPrice && discPct > 0 ? Math.round(rawPrice * (1 - discPct / 100)) : rawPrice;

    return {
      price: rawPrice ? `₹${rawPrice.toLocaleString("en-IN")} / Night` : null,
      rawPrice: rawPrice,
      discountedPrice: discountedPrice,
      discountPercent: discPct,
      promoText: pText,
      totalInventory: total,
      bookedCount: booked,
      available: avail,
      isFull: isStatusFull || avail === 0,
      status: matched.status || (avail === 0 ? "Full" : "Available")
    };
  };

  // Render 5 stars (or custom count via item.starCount)
  const renderStars = (starCount) => {
    const stars = [];
    const count = starCount || 5;
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span
          key={i}
          className={`star ${i < count ? "filled" : "empty"}`}
          aria-hidden="true"
        >
          ★
        </span>
      );
    }
    return stars;
  };

  const handleBookNowClick = (roomType) => {
    setSelectedRoomType(roomType);
    setShowPopup(true);
    setSelectedDetailRoom(null);
  };

  return (
    <>
      <section className="rooms-section" aria-label="Rooms">
        <div className="container rooms-container">
          <CommonHeading
            heading="Our Rooms"
            title="Rooms"
            subtitle="Stay in Comfort at"
          />

          <div className="rooms-grid">
            {roomItems.map((item, key) => {
              const liveInfo = getRoomLiveInfo(item.name);
              const displayPrice = liveInfo.price || item.price;
              const isAvailable = !liveInfo.isFull;

              return (
                <article
                  className={`room-card ${!isAvailable ? "room-card--soldout" : ""}`}
                  key={key}
                  tabIndex={0}
                  aria-label={`Room: ${item.name}`}
                  style={!isAvailable ? { opacity: 0.9 } : {}}
                >
                  {/* Image block */}
                  <div className="image-wrapper" style={{ position: "relative" }}>
                    {/* PROMO / DISCOUNT BADGE ON IMAGE */}
                    {liveInfo.promoText && (
                      <div
                        className="promo-badge"
                        style={{
                          position: "absolute",
                          top: "15px",
                          left: "15px",
                          background: "linear-gradient(135deg, #FF416C, #FF4B2B)",
                          color: "#ffffff",
                          fontWeight: "bold",
                          fontSize: "0.78rem",
                          padding: "6px 14px",
                          borderRadius: "20px",
                          boxShadow: "0 4px 14px rgba(255, 75, 43, 0.4)",
                          zIndex: 5,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px"
                        }}
                      >
                        <i className="fa fa-fire" /> {liveInfo.promoText}
                      </div>
                    )}

                    <img
                      src={item.img}
                      alt={item.name}
                      loading="lazy"
                      className="room-img"
                      style={!isAvailable ? { filter: "grayscale(30%)" } : {}}
                    />
                    <div className="price-tag d-flex align-items-center gap-2">
                      {liveInfo.discountPercent > 0 && liveInfo.rawPrice ? (
                        <>
                          <span style={{ textDecoration: "line-through", opacity: 0.75, fontSize: "0.85rem", color: "#ddd" }}>
                            ₹{liveInfo.rawPrice.toLocaleString("en-IN")}
                          </span>
                          <span style={{ color: "#FEA116", fontWeight: "bold" }}>
                            ₹{liveInfo.discountedPrice.toLocaleString("en-IN")} <small style={{ fontSize: "0.7rem", opacity: 0.8 }}>/ Night</small>
                          </span>
                        </>
                      ) : (
                        displayPrice
                      )}
                    </div>
                    <div className="image-gradient" />
                  </div>

                  {/* Content block */}
                  <div className="content">
                    <header className="room-header">
                      <h3>{item.name}</h3>
                      <div className="stars">
                        {renderStars(item.starCount)}
                      </div>
                    </header>

                    {/* LIVE REAL-TIME AVAILABILITY BADGE */}
                    <div className="my-2">
                      {isAvailable ? (
                        <span
                          className="badge px-3 py-2 rounded-pill fw-bold"
                          style={{
                            background: "rgba(46, 204, 113, 0.15)",
                            color: "#27ae60",
                            border: "1.5px solid #2ecc71",
                            fontSize: "0.82rem",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px"
                          }}
                        >
                          <i className="fa fa-check-circle" />
                          {liveInfo.available} Rooms Currently Available
                        </span>
                      ) : (
                        <span
                          className="badge px-3 py-2 rounded-pill fw-bold"
                          style={{
                            background: "rgba(231, 76, 60, 0.15)",
                            color: "#e74c3c",
                            border: "1.5px solid #e74c3c",
                            fontSize: "0.82rem",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px"
                          }}
                        >
                          <i className="fa fa-ban" />
                          Fully Booked / Sold Out
                        </span>
                      )}
                    </div>

                    <div className="facilities">
                      {facility.map((fItem, index) => (
                        <small
                          key={index}
                          className="facility-item"
                          aria-label={fItem.facility}
                        >
                          {fItem.icon} {fItem.quantity && `${fItem.quantity} `}{fItem.facility}
                        </small>
                      ))}
                    </div>

                    <p className="description">{item.description}</p>

                    <div className="btn-group">
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={() => setSelectedDetailRoom({ ...item, liveInfo, displayPrice })}
                        aria-label={`View details of ${item.name}`}
                      >
                        {item.yellowbtn}
                      </button>
                      <button
                        className={`btn ${isAvailable ? "btn-secondary" : "btn-dark text-white-50"}`}
                        type="button"
                        onClick={() => isAvailable && handleBookNowClick(item.name)}
                        disabled={!isAvailable}
                        aria-label={isAvailable ? `Book now for ${item.name}` : `${item.name} is fully booked`}
                        style={!isAvailable ? { opacity: 0.65, cursor: "not-allowed", border: "1.5px solid #e74c3c" } : {}}
                      >
                        {isAvailable ? item.darkbtn : "SOLD OUT"}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Room Detail Modal */}
      {selectedDetailRoom && (
        <div
          className="unique-popup-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="room-detail-title"
          onClick={() => setSelectedDetailRoom(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(5, 14, 26, 0.85)",
            backdropFilter: "blur(8px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#0B1D37",
              color: "#FAF6F0",
              border: "1.5px solid rgba(212, 175, 55, 0.4)",
              borderRadius: "24px",
              maxWidth: "600px",
              width: "100%",
              overflow: "hidden",
              boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
              position: "relative"
            }}
          >
            <button
              type="button"
              onClick={() => setSelectedDetailRoom(null)}
              aria-label="Close detail modal"
              style={{
                position: "absolute",
                top: "15px",
                right: "15px",
                background: "rgba(255,255,255,0.15)",
                border: "none",
                color: "#fff",
                fontSize: "1.5rem",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                cursor: "pointer",
                zIndex: 10
              }}
            >
              ×
            </button>
            <div style={{ height: "240px", overflow: "hidden", position: "relative" }}>
              <img
                src={selectedDetailRoom.img}
                alt={selectedDetailRoom.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "15px",
                  left: "20px",
                  background: "linear-gradient(135deg, #1E3E72, #0B1D37)",
                  color: "#FEA116",
                  padding: "6px 16px",
                  borderRadius: "30px",
                  fontWeight: "bold",
                  border: "1px solid rgba(212, 175, 55, 0.5)"
                }}
              >
                {selectedDetailRoom.price}
              </div>
            </div>
            <div style={{ padding: "25px" }}>
              <h2 id="room-detail-title" style={{ fontFamily: "'Cinzel', serif", color: "#FEA116", marginBottom: "10px" }}>
                {selectedDetailRoom.name}
              </h2>
              <p style={{ lineHeight: "1.6", opacity: 0.9, marginBottom: "20px" }}>
                {selectedDetailRoom.description}
              </p>

              <h4 style={{ fontSize: "1rem", color: "#D4AF37", marginBottom: "10px" }}>Key Amenities Included:</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "25px" }}>
                <span style={{ background: "rgba(255,255,255,0.08)", padding: "6px 12px", borderRadius: "8px", fontSize: "0.85rem" }}>🛏️ Plush King Bedding</span>
                <span style={{ background: "rgba(255,255,255,0.08)", padding: "6px 12px", borderRadius: "8px", fontSize: "0.85rem" }}>📶 High-Speed Wi-Fi</span>
                <span style={{ background: "rgba(255,255,255,0.08)", padding: "6px 12px", borderRadius: "8px", fontSize: "0.85rem" }}>❄️ Climate Air Conditioning</span>
                <span style={{ background: "rgba(255,255,255,0.08)", padding: "6px 12px", borderRadius: "8px", fontSize: "0.85rem" }}>🏊 Pool & Lawn Access</span>
                <span style={{ background: "rgba(255,255,255,0.08)", padding: "6px 12px", borderRadius: "8px", fontSize: "0.85rem" }}>☕ Complimentary Breakfast</span>
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  type="button"
                  onClick={() => handleBookNowClick(selectedDetailRoom.name)}
                  style={{
                    flex: 1,
                    background: "linear-gradient(135deg, #FEA116, #D4AF37)",
                    color: "#050E1A",
                    fontWeight: "bold",
                    padding: "12px",
                    borderRadius: "50px",
                    border: "none",
                    cursor: "pointer"
                  }}
                >
                  Reserve {selectedDetailRoom.name}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPopup && (
        <Popup
          show={showPopup}
          onClose={() => setShowPopup(false)}
          initialRoomType={selectedRoomType}
        />
      )}

      {/* Rajasthani themed + fully responsive styles */}
      <style>{`
        /* Section background – aligned with Services */
        .rooms-section {
          padding: 32px 0;
          background: linear-gradient(
            180deg,
            #FAF6F0 0%,
            #F2EAE0 50%,
            #FAF6F0 100%
          );
        }

        .rooms-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        /* Grid: 3 desktop, 2 tablet, 1 mobile */
        .rooms-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 2.2rem;
          margin-top: 1.5rem;
        }

        @media (max-width: 991.98px) {
          .rooms-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 1.8rem;
          }
        }

        @media (max-width: 575.98px) {
          .rooms-grid {
            grid-template-columns: 1fr;
            gap: 1.4rem;
          }
        }

        /* Room card */
        .room-card {
          background: rgba(255, 255, 255, 0.98);
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 12px 32px rgba(11, 29, 55, 0.08);
          display: flex;
          flex-direction: column;
          transition: transform 0.35s ease, box-shadow 0.35s ease,
            border-color 0.35s ease;
          cursor: pointer;
          border: 1.5px solid rgba(30, 62, 114, 0.12);
          outline: none;
        }

        .room-card:hover,
        .room-card:focus {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 48px rgba(30, 62, 114, 0.18);
          border-color: rgba(212, 175, 55, 0.65);
        }

        /* Image */
        .image-wrapper {
          position: relative;
          overflow: hidden;
          height: 230px;
          background: #0B1D37;
        }

        .room-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease, filter 0.5s ease;
          filter: brightness(0.95);
        }

        .room-card:hover .room-img {
          transform: scale(1.06);
          filter: brightness(1);
        }

        .price-tag {
          position: absolute;
          top: 1rem;
          left: 1rem;
          background: linear-gradient(135deg, #1E3E72, #0B1D37);
          color: #FEA116;
          padding: 0.4rem 1.2rem;
          font-weight: 700;
          border-radius: 30px;
          font-size: 0.95rem;
          border: 1px solid rgba(212, 175, 55, 0.5);
          box-shadow: 0 6px 16px rgba(11, 29, 55, 0.4);
          z-index: 2;
          letter-spacing: 0.06em;
        }

        .image-gradient {
          position: absolute;
          bottom: 0;
          width: 100%;
          height: 4.5rem;
          background: linear-gradient(
            180deg,
            transparent,
            rgba(11, 29, 55, 0.5)
          );
          pointer-events: none;
          z-index: 1;
        }

        /* Content */
        .content {
          padding: 1.8rem 2rem 2.1rem;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          color: #1A283B;
          font-family: 'Poppins', sans-serif;
        }

        .room-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.7rem;
        }

        .room-header h3 {
          font-size: 1.35rem;
          font-weight: 800;
          color: #0B1D37;
          margin: 0;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          font-family: "Cinzel", serif;
        }

        /* Stars */
        .stars {
          font-size: 1.1rem;
          user-select: none;
        }

        .star {
          margin-left: 3px;
        }

        .star.filled {
          color: #FEA116;
        }

        .star.empty {
          color: #d1d5db;
        }

        /* Facilities row */
        .facilities {
          display: flex;
          flex-wrap: wrap;
          gap: 0.7rem;
          margin-bottom: 1.1rem;
        }

        .facility-item {
          font-size: 0.85rem;
          color: #53647C;
          border-right: 1px solid rgba(30, 62, 114, 0.15);
          padding-right: 0.6rem;
          font-weight: 500;
          white-space: nowrap;
        }

        .facility-item:last-child {
          border-right: none;
        }

        .facility-item i {
          color: #FEA116 !important;
        }

        /* Description */
        .description {
          font-size: 0.94rem;
          color: #53647C;
          flex-grow: 1;
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        /* Buttons */
        .btn-group {
          display: flex;
          gap: 0.8rem;
          justify-content: center;
          flex-wrap: nowrap;
        }

        .btn {
          cursor: pointer;
          font-weight: 700;
          padding: 0.6rem 1.4rem;
          border-radius: 999px;
          border: none;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          letter-spacing: 0.04em;
          white-space: nowrap;
          min-width: 110px;
        }

        .btn-primary {
          background: transparent !important;
          border: 1.5px solid #1E3E72 !important;
          color: #1E3E72 !important;
        }

        .btn-primary:hover,
        .btn-primary:focus {
          background: #1E3E72 !important;
          color: #FAF6F0 !important;
          transform: translateY(-1px) !important;
        }

        .btn-secondary {
          background: linear-gradient(135deg, #FEA116, #D4AF37);
          color: #050E1A;
          box-shadow: 0 4px 14px rgba(254, 161, 22, 0.3);
        }

        .btn-secondary:hover,
        .btn-secondary:focus {
          background: linear-gradient(135deg, #ff8c00, #fea116);
          box-shadow: 0 6px 20px rgba(254, 161, 22, 0.5);
          transform: translateY(-1px);
        }

        /* Medium screens */
        @media (max-width: 991.98px) {
          .content {
            padding: 1.6rem 1.6rem 1.9rem;
          }

          .room-header h3 {
            font-size: 1.3rem;
          }

          .description {
            font-size: 0.93rem;
          }
        }

        /* Mobile */
        @media (max-width: 767.98px) {
          .image-wrapper {
            height: 210px;
          }

          .content {
            padding: 1.4rem 1.3rem 1.7rem;
          }

          .room-header h3 {
            font-size: 1.15rem;
            letter-spacing: 0.06em;
          }

          .stars {
            font-size: 1rem;
          }

          .facilities {
            gap: 0.5rem;
          }

          .facility-item {
            font-size: 0.85rem;
          }

          .description {
            font-size: 0.9rem;
            margin-bottom: 1.2rem;
          }

          .btn {
            font-size: 0.85rem;
            padding: 0.45rem 1rem;
            min-width: 90px;
          }
        }

        /* Very small mobiles */
        @media (max-width: 400px) {
          .rooms-grid {
            gap: 1rem;
          }

          .room-card {
            border-radius: 18px;
          }

          .image-wrapper {
            height: 185px;
          }

          .content {
            padding: 1.1rem 1rem 1.4rem;
          }

          .btn-group {
            gap: 0.6rem;
          }

          .btn {
            font-size: 0.82rem;
            padding: 0.4rem 0.8rem;
            flex-grow: 1;
            white-space: normal;
            text-align: center;
          }
        }
      `}</style>
    </>
  );
}
